import {
  type HtmlPageOrientation,
  type HtmlPagePaper,
  NAMED_PAPERS,
  ORIENTATION_LABELS,
} from '@app/shared'
import { sheetMm } from '@segnavia/format'

export type HtmlPagePaperGeometry = { widthMm: number; heightMm: number }

/**
 * A paper's size as the sheet prints it. A custom paper's two sides are a pair
 * with no order of their own, so its size is stated across then down, after the
 * orientation has placed them.
 */
export function htmlPagePaperLabel(
  paper: HtmlPagePaper | null,
  orientation: HtmlPageOrientation,
): string {
  if (!paper) return 'Screen page'
  if (paper.name !== 'custom') return NAMED_PAPERS[paper.name].label
  const sheet = orient(
    { widthMm: paper.widthMm, heightMm: paper.heightMm },
    orientation,
  )
  return `${sheet.widthMm} × ${sheet.heightMm} mm`
}

export function htmlPageSheetLabel(
  paper: HtmlPagePaper | null,
  orientation: HtmlPageOrientation,
): string {
  const label = htmlPagePaperLabel(paper, orientation)
  return paper
    ? `${label}, ${ORIENTATION_LABELS[orientation].toLocaleLowerCase()}`
    : label
}

export function htmlPagePaperGeometry(
  paper: HtmlPagePaper | null,
): HtmlPagePaperGeometry | undefined {
  if (!paper) return undefined
  return paper.name === 'custom'
    ? { widthMm: paper.widthMm, heightMm: paper.heightMm }
    : {
        widthMm: NAMED_PAPERS[paper.name].widthMm,
        heightMm: NAMED_PAPERS[paper.name].heightMm,
      }
}

export function htmlPageSheetGeometry(
  paper: HtmlPagePaperGeometry | null | undefined,
  orientation: HtmlPageOrientation,
): HtmlPagePaperGeometry | undefined {
  return paper ? orient(paper, orientation) : undefined
}

/** Segnavia's rule, the one the PDF is sized by: landscape puts the longer side
 *  across and portrait the shorter, whatever order the sides were given in. */
function orient(
  paper: HtmlPagePaperGeometry,
  orientation: HtmlPageOrientation,
): HtmlPagePaperGeometry {
  const sheet = sheetMm({
    paper: { width: paper.widthMm, height: paper.heightMm, unit: 'mm' },
    orientation,
  })
  return { widthMm: sheet.width, heightMm: sheet.height }
}
